import ManagementPageHTML, {
  IdentityCell,
} from "../../components/ManagementPage/ManagementPageHTML";
import type {
  ManagementColumn,
  ManagementDialogField,
} from "../../components/ManagementPage/ManagementPageTypes";
import type { DailySalesSummary } from "../../models/DailySalesSummary";
import { ReportService } from "../../services/ReportService";

const money = new Intl.NumberFormat("en-NG", {
  style: "currency",
  currency: "NGN",
});
const columns: ManagementColumn<DailySalesSummary>[] = [
  {
    Key: "Date",
    Header: "Business date",
    Render: (row) => (
      <IdentityCell
        Icon="fa-calendar-day"
        Title={new Date(row.BusinessDate).toLocaleDateString("en-NG", {
          dateStyle: "medium",
        })}
        Subtitle={row.Code}
      />
    ),
  },
  { Key: "Sales", Header: "Sales", Render: (row) => row.TotalSalesCount },
  {
    Key: "Gross",
    Header: "Gross sales",
    Render: (row) => money.format(row.GrossSales),
  },
  {
    Key: "Discount",
    Header: "Discount",
    Render: (row) => money.format(row.DiscountAmount),
  },
  {
    Key: "Net",
    Header: "Net sales",
    Render: (row) => <strong>{money.format(row.NetSales)}</strong>,
  },
  {
    Key: "Income",
    Header: "Income",
    Render: (row) => money.format(row.Income),
  },
  {
    Key: "Expenses",
    Header: "Expenses",
    Render: (row) => money.format(row.Expenses),
  },
  {
    Key: "CashFlow",
    Header: "Net cash flow",
    Render: (row) => <strong>{money.format(row.NetCashFlow)}</strong>,
  },
];
const fields: ManagementDialogField<DailySalesSummary>[] = [
  { Key: "Code", Label: "Report code", DisableAfterNew: true },
  { Key: "BusinessDate", Label: "Business date", Type: "date", Required: true },
  {
    Key: "TotalSalesCount",
    Label: "Sales count",
    Type: "integer",
    Required: true,
  },
  { Key: "GrossSales", Label: "Gross sales", Type: "money", Required: true },
  { Key: "DiscountAmount", Label: "Discount", Type: "money" },
  { Key: "NetSales", Label: "Net sales", Type: "money", Required: true },
  { Key: "Income", Label: "Income", Type: "money" },
  { Key: "Expenses", Label: "Expenses", Type: "money" },
  { Key: "NetCashFlow", Label: "Net cash flow", Type: "money" },
  { Key: "CurrencyCode", Label: "Currency", Required: true },
  {
    Key: "GeneratedAt",
    Label: "Generated at",
    Type: "datetime",
    Required: true,
  },
];
export default function DailySalesSummaryHTML() {
  return (
    <ManagementPageHTML
      Service={ReportService()}
      DialogFields={fields}
      CanCreate={false} CanEdit={false} CanDelete={false}
      Title="Daily sales summaries"
      Eyebrow="Sales reporting"
      Description="Review backend-generated daily revenue, expense and cash-flow summaries. Report generation requires a backend aggregation endpoint."
      AddLabel="Generate summary"
      SearchPlaceholder="Search report date"
      Rows={[]}
      Columns={columns}
      GetRowKey={(row) => row.Code}
      GetSearchText={(row) => `${row.Code} ${row.BusinessDate}`}
      Summaries={[]}
      GetSummaries={(rows, total) => [
        {
          Label: "Reports",
          Value: total,
          Icon: "fa-chart-column",
          Tone: "Blue",
        },
        {
          Label: "Net sales",
          Value: money.format(rows.reduce((s, x) => s + x.NetSales, 0)),
          Icon: "fa-naira-sign",
          Tone: "Green",
        },
        {
          Label: "Expenses",
          Value: money.format(rows.reduce((s, x) => s + x.Expenses, 0)),
          Icon: "fa-arrow-trend-down",
          Tone: "Red",
        },
        {
          Label: "Net cash flow",
          Value: money.format(rows.reduce((s, x) => s + x.NetCashFlow, 0)),
          Icon: "fa-chart-line",
          Tone: "Purple",
        },
      ]}
      Filters={[
        {
          Key: "Currency",
          Label: "Currency",
          Options: ["NGN", "USD", "GBP"],
          GetValue: (row) => row.CurrencyCode,
        },
      ]}
    />
  );
}
